import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import {
  faCheck,
  faStar,
  faStarHalfStroke,
} from "@fortawesome/pro-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Text from "./shared/Text";

/**
 * PricingHeroV2 — two-column pricing hero.
 *
 * LEFT  — split title (muted + bright), description, 2-col checklist
 *         of plan inclusions, "Trusted by" label, row of review badges.
 * RIGHT — soft rounded card containing a hero image, "Starts at" label,
 *         struck-through original price + "Free" + duration line, and
 *         a CTA pair.
 */

export type PricingHeroReviewBadge = {
  name: string;
  iconSrc?: string;
  rating: number;
};

export type PricingHeroV2Data = {
  titleMuted: string;
  titleBright: string;
  description: string;
  bullets: string[];
  trustedByLabel?: string;
  reviewBadges: PricingHeroReviewBadge[];
  card: {
    image?: string;
    imageAlt?: string;
    startsAtLabel: string;
    strikePrice: string;
    freeLabel: string;
    durationLabel: string;
    ctas: CtaType[];
  };
};

const Stars = ({ rating }: { rating: number }) => {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.25 && rating - full < 0.75;
  const fullCount = rating - full >= 0.75 ? full + 1 : full;
  return (
    <div className="flex items-center gap-1" aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => {
        if (i < fullCount) {
          return (
            <FontAwesomeIcon
              key={i}
              icon={faStar}
              className="h-3.5 w-3.5 text-[#F5A623]"
              aria-hidden="true"
            />
          );
        }
        if (i === fullCount && hasHalf) {
          return (
            <FontAwesomeIcon
              key={i}
              icon={faStarHalfStroke}
              className="h-3.5 w-3.5 text-[#F5A623]"
              aria-hidden="true"
            />
          );
        }
        return (
          <FontAwesomeIcon
            key={i}
            icon={faStarRegular}
            className="h-3.5 w-3.5 text-neutralBlack/20"
            aria-hidden="true"
          />
        );
      })}
    </div>
  );
};

const CheckBullet = ({ text }: { text: string }) => (
  <li className="flex items-start gap-3">
    <span
      className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#5766F2]"
      aria-hidden="true"
    >
      <FontAwesomeIcon
        icon={faCheck}
        className="h-2.5 w-2.5 text-white"
      />
    </span>
    <Text
      as="span"
      variant="body-md"
      className="text-[13px] leading-snug text-neutralBlack md:whitespace-nowrap"
    >
      {text}
    </Text>
  </li>
);

const PricingHeroV2 = ({
  titleMuted,
  titleBright,
  description,
  bullets,
  trustedByLabel = "Trusted by 200,000+ creators",
  reviewBadges,
  card,
}: PricingHeroV2Data) => {
  return (
    <section className="relative w-full overflow-hidden bg-white">
      <div className="container relative z-10 mx-auto px-4 pb-12 pt-8 sm:px-6 md:px-8 md:pb-16 md:pt-12 lg:px-12 lg:pb-20 lg:pt-16">
        <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-14">
          {/* LEFT column */}
          <div>
            <Text
              as="h1"
              variant="display-lg"
              className="text-[24px] sm:text-[24px] md:text-[44px] lg:text-[48px]"
            >
              <span className="text-neutralBlack">{titleMuted}</span>
              <br />
              <span className="text-neutralBlack">{titleBright}</span>
            </Text>

            <Text
              as="p"
              variant="body-lg"
              className="mt-5 max-w-xl text-[14px] leading-relaxed text-darkSlate04 md:mt-6 md:text-[16px]"
            >
              {description}
            </Text>

            {bullets && bullets.length ? (
              <ul className="mt-8 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 md:mt-10">
                {bullets.map((b) => (
                  <CheckBullet key={b} text={b} />
                ))}
              </ul>
            ) : null}

            {trustedByLabel ? (
              <Text
                as="p"
                variant="body-md"
                className="mt-10 text-[14px] font-semibold text-[#5766F2] md:mt-12"
              >
                {trustedByLabel}
              </Text>
            ) : null}

            {reviewBadges && reviewBadges.length ? (
              <div className="mt-4 flex flex-wrap items-center gap-8">
                {reviewBadges.map((b) => (
                  <div
                    key={b.name}
                    className="flex flex-col items-start gap-1.5"
                  >
                    {b.iconSrc ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={b.iconSrc}
                        alt={b.name}
                        className="h-5 object-contain"
                      />
                    ) : (
                      <Text
                        as="div"
                        variant="body-md"
                        className="text-[14px] font-semibold text-neutralBlack"
                      >
                        {b.name}
                      </Text>
                    )}
                    <Stars rating={b.rating} />
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* RIGHT column — pricing card */}
          <div>
            <article className="rounded-3xl border border-neutralBlack/5 bg-[#F3F4F8] p-4 shadow-[0_12px_40px_rgba(20,20,40,0.06)] sm:p-5 md:p-6">
              {card.image ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={card.image}
                  alt={card.imageAlt ?? ""}
                  decoding="async"
                  {...({ fetchpriority: "high" } as any)}
                  className="block aspect-[16/9] w-full rounded-2xl object-cover"
                />
              ) : (
                <div className="aspect-[16/9] w-full rounded-2xl bg-gradient-to-br from-slateLight via-white to-slateLight" />
              )}

              <div className="px-2 pb-2 pt-6 md:px-4 md:pt-8">
                <Text
                  as="div"
                  variant="body-md"
                  className="text-[14px] text-darkSlate04 md:text-[15px]"
                >
                  {card.startsAtLabel}
                </Text>

                <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="relative font-inter text-[22px] font-medium text-darkSlate04 md:text-[26px]">
                    <span className="opacity-70">{card.strikePrice}</span>
                    {/* Diagonal strike-through in the brand purple */}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-x-[-4px] top-1/2 block h-[2.5px] -translate-y-1/2 rotate-[-12deg] rounded-full bg-[#5766F2]"
                    />
                  </span>
                  <Text
                    as="span"
                    variant="display-lg"
                    className="text-[24px] font-bold text-neutralBlack md:text-[48px]"
                  >
                    {card.freeLabel}
                  </Text>
                  <Text
                    as="span"
                    variant="body-md"
                    className="text-[14px] text-darkSlate04 md:text-[15px]"
                  >
                    {card.durationLabel}
                  </Text>
                </div>

                {card.ctas && card.ctas.length ? (
                  <div className="mt-6 md:mt-8">
                    <CtaButtonGroup
                      ctas={card.ctas}
                      gap="flex flex-wrap items-center gap-3"
                      fullWidthOnMobile={false}
                    />
                  </div>
                ) : null}
              </div>
            </article>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};

export default PricingHeroV2;
